import React from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Link,
  Stack,
  IconButton
} from '@mui/material';
import {
  Facebook,
  Twitter,
  LinkedIn
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import LogoImage from '../assets/logo0.png';

const FooterLink = styled(Link)({
  color: '#fff',
  textDecoration: 'none',
  fontSize: '14px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: 0,
    width: '0',
    height: '2px',
    backgroundColor: '#FFE302',
    transition: 'width 0.3s ease-in-out'
  },
  '&:hover': {
    color: '#FFE302',
    textDecoration: 'none',
    '&::after': {
      width: '100%'
    }
  }
});

const QuickLink = styled(Link)({
  color: '#fff',
  textDecoration: 'none',
  fontSize: '14px',
  position: 'relative',
  transition: 'color 0.3s ease-in-out',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: 0,
    width: '0',
    height: '2px',
    backgroundColor: '#FFE302',
    transition: 'width 0.3s ease-in-out'
  },
  '&:hover': {
    color: '#FFE302',
    '&::after': {
      width: '100%'
    }
  }
});

const SocialIconButton = styled(IconButton)({
  color: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  }
});

const TableLink = styled(Link)({
  color: '#242729',
  textDecoration: 'none',
  fontSize: '15px',
  padding: '12px 16px',
  transition: 'all 0.2s ease-in-out',
  borderLeft: '3px solid transparent',
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  position: 'relative',
  '&:hover': {
    color: '#0074CC',
    borderLeft: '3px solid #0074CC',
    backgroundColor: '#F8F9F9'
  },
  '&:not(:last-child)': {
    borderBottom: '1px solid #E4E6E8'
  }
});

const TOCContainer = styled(Box)({
  backgroundColor: '#fff',
  border: '1px solid #E4E6E8',
  borderRadius: '3px',
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
});

const About = () => {
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: '#fff'
    }}>
      {/* Header with Logo */}
      <Box 
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#000000'
        }}
      >
        <Box 
          sx={{
            maxWidth: '800px',
            width: '100%',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Link to="/" style={{ textDecoration: 'none' }}>
            <img 
              src={LogoImage} 
              alt="Larvarris AI Logo" 
              style={{ 
                height: '30px',
                filter: 'brightness(0) invert(1)'
              }}
            />
          </Link>
        </Box>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ flex: 1, py: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 4 }}>
              <img
                src={LogoImage}
                alt="Larvarris AI Logo"
                style={{
                  height: '30px',
                  width: 'auto',
                  marginBottom: '2rem',
                  filter: 'brightness(0) invert(1)'
                }}
              />
              <Typography variant="h3" sx={{ 
                fontWeight: 600,
                mb: 3,
                color: '#000'
              }}>
                Empowering Smart Repairs with AI
              </Typography>
              <Typography variant="body1" sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: '#000',
                opacity: 0.8,
                mb: 4
              }}>
                At Larvarris AI, we are revolutionizing the way people diagnose and repair their devices. Our innovative AI-powered platform provides smart, remote repair solutions for mobile phones, computers, and household appliances. We believe in making technical troubleshooting simpler, faster, and more cost-effective by integrating artificial intelligence, cloud-based service tracking, and expert consultations—all in one place.
              </Typography>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography variant="h4" sx={{ 
                fontWeight: 600,
                mb: 3,
                color: '#000'
              }}>
                Our Mission
              </Typography>
              <Typography variant="body1" sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: '#000',
                opacity: 0.8
              }}>
                We aim to eliminate the frustrations of traditional repairs by giving users the tools to diagnose and fix their devices seamlessly. Whether it's detecting hardware malfunctions, connecting with certified technicians, or purchasing genuine spare parts, Larvarris AI ensures a hassle-free repair experience from start to finish.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: '#f5f5f5',
                p: 4,
                borderRadius: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Quick Links Section */}
              <Typography variant="h5" sx={{ mb: 4, color: '#000' }}>
                Quick Links
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={6}>
                  <Stack spacing={2}>
                    <Link href="/hardware" sx={{
                      color: '#000',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#007C3F'
                      }
                    }}>
                      Hardware
                    </Link>
                    <Link href="/guidelines" sx={{
                      color: '#000',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#007C3F'
                      }
                    }}>
                      Guidelines
                    </Link>
                    <Link href="/contact" sx={{
                      color: '#000',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#007C3F'
                      }
                    }}>
                      Contact
                    </Link>
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Stack spacing={2}>
                    <Link href="/privacy" sx={{
                      color: '#000',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#007C3F'
                      }
                    }}>
                      Privacy
                    </Link>
                    <Link href="/terms" sx={{
                      color: '#000',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#007C3F'
                      }
                    }}>
                      Terms
                    </Link>
                    <Link href="/support" sx={{
                      color: '#000',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#007C3F'
                      }
                    }}>
                      Support
                    </Link>
                  </Stack>
                </Grid>
              </Grid>

              {/* Table of Contents Section */}
              <Box sx={{ mt: 4 }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    mb: 3, 
                    color: '#242729',
                    fontFamily: 'Roboto Slab, serif',
                    fontWeight: 700,
                    fontSize: '20px'
                  }}
                >
                  Table of Contents
                </Typography>
                
                <TOCContainer>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: '#F8F9F9',
                    borderBottom: '1px solid #E4E6E8'
                  }}>
                    <Typography 
                      sx={{ 
                        color: '#6A737C',
                        fontSize: '13px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Jump to Section
                    </Typography>
                  </Box>

                  <Box>
                    <TableLink href="#introduction">
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2 
                      }}>
                        <Typography 
                          component="span" 
                          sx={{ 
                            color: '#6A737C',
                            fontSize: '14px',
                            width: '24px' 
                          }}
                        >
                          01
                        </Typography>
                        Introduction to Larvarris AI
                      </Box>
                    </TableLink>

                    <TableLink href="#ai-diagnostics">
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2 
                      }}>
                        <Typography 
                          component="span" 
                          sx={{ 
                            color: '#6A737C',
                            fontSize: '14px',
                            width: '24px' 
                          }}
                        >
                          02
                        </Typography>
                        AI-Powered Diagnostics
                      </Box>
                    </TableLink>

                    <TableLink href="#expert-consultations">
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2 
                      }}>
                        <Typography 
                          component="span" 
                          sx={{ 
                            color: '#6A737C',
                            fontSize: '14px',
                            width: '24px' 
                          }}
                        >
                          03
                        </Typography>
                        Expert Video Consultations
                      </Box>
                    </TableLink>

                    <TableLink href="#repair-guides">
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2 
                      }}>
                        <Typography 
                          component="span" 
                          sx={{ 
                            color: '#6A737C',
                            fontSize: '14px',
                            width: '24px' 
                          }}
                        >
                          04
                        </Typography>
                        Step-by-Step Repair Guides
                      </Box>
                    </TableLink>

                    <TableLink href="#marketplace">
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2 
                      }}>
                        <Typography 
                          component="span" 
                          sx={{ 
                            color: '#6A737C',
                            fontSize: '14px',
                            width: '24px' 
                          }}
                        >
                          05
                        </Typography>
                        Genuine Parts Marketplace
                      </Box>
                    </TableLink>

                    <TableLink href="#support-services">
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2 
                      }}>
                        <Typography 
                          component="span" 
                          sx={{ 
                            color: '#6A737C',
                            fontSize: '14px',
                            width: '24px' 
                          }}
                        >
                          06
                        </Typography>
                        Support Services
                      </Box>
                    </TableLink>

                    <TableLink href="#company-mission">
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2 
                      }}>
                        <Typography 
                          component="span" 
                          sx={{ 
                            color: '#6A737C',
                            fontSize: '14px',
                            width: '24px' 
                          }}
                        >
                          07
                        </Typography>
                        Our Mission & Vision
                      </Box>
                    </TableLink>

                    <TableLink href="#contact-info">
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        gap: 2 
                      }}>
                        <Typography 
                          component="span" 
                          sx={{ 
                            color: '#6A737C',
                            fontSize: '14px',
                            width: '24px' 
                          }}
                        >
                          08
                        </Typography>
                        Contact Information
                      </Box>
                    </TableLink>
                  </Box>
                </TOCContainer>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ 
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        py: 3 
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ color: '#fff', opacity: 0.8 }}>
                © 2024 Larvarris AI. All rights reserved.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent="center">
                <SocialIconButton>
                  <Facebook />
                </SocialIconButton>
                <SocialIconButton>
                  <Twitter />
                </SocialIconButton>
                <SocialIconButton>
                  <LinkedIn />
                </SocialIconButton>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <FooterLink href="/privacy">Privacy Policy</FooterLink>
                <FooterLink href="/cookie-settings">Cookie Settings</FooterLink>
                <FooterLink href="/terms">Terms</FooterLink>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default About;
